import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import BellIcon from './BellIcon';
import { getNotificationActionPath, isTipNotification } from '../utils/notificationRouting';
import { getListingPresentation } from '../utils/hostListings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 12000;
const SHOWN_TIP_NOTIFICATION_IDS_STORAGE_KEY = 'shown-tip-notification-ids';
// One-time "you can swipe through stacked alerts" hint on the popup dots.
const SWIPE_HINT_SEEN_KEY = 'notif-swipe-hint-seen';

const loadShownTipNotificationIds = () => {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const storedValue = window.localStorage.getItem(SHOWN_TIP_NOTIFICATION_IDS_STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    return new Set(Array.isArray(parsedValue) ? parsedValue : []);
  } catch {
    return new Set();
  }
};

const persistShownTipNotificationIds = (ids) => {
  if (typeof window === 'undefined') {
    return;
  }

  const trimmedIds = Array.from(ids).slice(-60);
  window.localStorage.setItem(SHOWN_TIP_NOTIFICATION_IDS_STORAGE_KEY, JSON.stringify(trimmedIds));
};

const updateStoredUser = (nextUser) => {
  if (!nextUser) {
    return;
  }
  localStorage.setItem('user', JSON.stringify(nextUser));
};

export const NotificationBellButton = ({ historyPath = '/notifications', buttonTestId = 'notification-bell-button', popupTestId = 'notification-popup' }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ unread_count: 0, latest_unread: null, latest_unread_notifications: [], push_buckets: { operational_alerts_push: true, vibes_reminders_push: true } });
  const [openPopup, setOpenPopup] = useState(false);
  const [popupQueue, setPopupQueue] = useState([]);
  const [activeNotificationIndex, setActiveNotificationIndex] = useState(0);
  const shownToastIdsRef = useRef(loadShownTipNotificationIds());
  const viewedPopupIdsRef = useRef(new Set());
  const wrapperRef = useRef(null);
  const sliderRef = useRef(null);
  // Per-slide inner content nodes — measured to size the slider to the ACTIVE
  // slide so the "cloud" hugs the text instead of stretching to the tallest one.
  const contentRefs = useRef({});
  const [sliderHeight, setSliderHeight] = useState('auto');
  // First time a multi-notification popup opens, the dots shimmer in a wave to
  // hint that the stack is swipeable. Shown once per device.
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const markToastAsShown = useCallback((notificationId) => {
    if (!notificationId) {
      return;
    }

    shownToastIdsRef.current.add(notificationId);
    persistShownTipNotificationIds(shownToastIdsRef.current);
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const response = await axios.get(`${API}/notifications/summary`, { headers });
      setSummary({
        unread_count: response.data.unread_count || 0,
        latest_unread: response.data.latest_unread || null,
        latest_unread_notifications: response.data.latest_unread_notifications || [],
        push_buckets: response.data.push_buckets || { operational_alerts_push: true, vibes_reminders_push: true },
      });
      if (response.data.user) {
        updateStoredUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to load notification summary:', error);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchSummary();
    const interval = window.setInterval(fetchSummary, POLL_INTERVAL);
    return () => window.clearInterval(interval);
  }, [fetchSummary]);

  const closePopup = useCallback(() => {
    setOpenPopup(false);
    setPopupQueue([]);
    setActiveNotificationIndex(0);
    viewedPopupIdsRef.current.clear();
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    if (!notificationId || !token) {
      return;
    }

    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, { headers });
      await fetchSummary();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [fetchSummary, headers, token]);

  const markNotificationAsSeen = useCallback(async (notificationId) => {
    if (!notificationId || !token) {
      return;
    }

    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, { headers });
      setSummary((prev) => {
        const remainingNotifications = (prev.latest_unread_notifications || []).filter((item) => item?.id !== notificationId);
        return {
          ...prev,
          unread_count: Math.max(0, (prev.unread_count || 0) - 1),
          latest_unread_notifications: remainingNotifications,
          latest_unread: prev.latest_unread?.id === notificationId ? remainingNotifications[0] || null : prev.latest_unread,
        };
      });
    } catch (error) {
      console.error('Failed to mark popup notification as seen:', error);
    }
  }, [headers, token]);

  useEffect(() => {
    if (!openPopup) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        closePopup();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closePopup, openPopup]);

  const popupNotifications = openPopup ? popupQueue : (summary.latest_unread_notifications || []);
  const activeNotification = popupNotifications[activeNotificationIndex] || summary.latest_unread || null;

  const measureActiveSlide = useCallback(() => {
    const note = popupNotifications[activeNotificationIndex];
    const el = note && contentRefs.current[note.id];
    if (el) {
      setSliderHeight(el.offsetHeight);
    }
  }, [activeNotificationIndex, popupNotifications]);

  useLayoutEffect(() => {
    if (!openPopup) return;
    measureActiveSlide();
  }, [openPopup, activeNotificationIndex, popupNotifications, measureActiveSlide]);

  useEffect(() => {
    if (!openPopup) return undefined;
    const onResize = () => measureActiveSlide();
    window.addEventListener('resize', onResize);
    // Re-measure shortly after open in case web fonts / images shift layout.
    const t = window.setTimeout(measureActiveSlide, 60);
    return () => {
      window.removeEventListener('resize', onResize);
      window.clearTimeout(t);
    };
  }, [openPopup, measureActiveSlide]);

  // One-time swipe-hint shimmer on the dots when a multi-notification popup opens.
  useEffect(() => {
    if (!openPopup || popupNotifications.length <= 1) return undefined;
    if (typeof window !== 'undefined' && window.localStorage.getItem(SWIPE_HINT_SEEN_KEY)) return undefined;
    setShowSwipeHint(true);
    const t = window.setTimeout(() => {
      setShowSwipeHint(false);
      try {
        window.localStorage.setItem(SWIPE_HINT_SEEN_KEY, '1');
      } catch {
        /* ignore storage errors */
      }
    }, 2600);
    return () => window.clearTimeout(t);
  }, [openPopup, popupNotifications.length]);

  const formatDisplayDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit' }).format(date);
  };

  const formatDateRange = (notification) => {
    const start = notification?.metadata?.check_in_date;
    const end = notification?.metadata?.checkout_date;
    if (!start || !end) return '';
    return `${formatDisplayDate(start)} — ${formatDisplayDate(end)}`;
  };

  const buildListingMeta = (notification) => {
    const metadata = notification?.metadata || {};
    return getListingPresentation({ title: metadata.listing_name || metadata.listing_title, address: metadata.listing_address || metadata.listing_location_label });
  };

  const isReservationNotification = (notification) => {
    const status = notification?.metadata?.status;
    // Legacy `detected` + 2026-02+ `confirmed_by_host` are both fresh
    // iCal reservations that must render as the rich reservation
    // card (dates read from metadata → European-formatted), never as
    // the raw notification body string.
    return notification?.metadata?.source === 'ical' && ['detected', 'confirmed_by_host'].includes(status);
  };

  const handlePopupActivate = useCallback(() => {
    const actionPath = getNotificationActionPath(activeNotification);
    if (!actionPath) {
      return;
    }

    closePopup();
    navigate(actionPath);
  }, [activeNotification, closePopup, navigate]);

  const handleBellClick = async () => {
    const unreadNotifications = summary.latest_unread_notifications || [];
    if (unreadNotifications.length) {
      if (openPopup) {
        closePopup();
        return;
      }

      viewedPopupIdsRef.current.clear();
      setPopupQueue(unreadNotifications);
      setActiveNotificationIndex(0);
      setOpenPopup(true);
      return;
    }

    navigate(historyPath);
  };

  const popupActionPath = getNotificationActionPath(activeNotification);

  useEffect(() => {
    if (!openPopup || !popupQueue.length) {
      return;
    }

    const currentNotification = popupQueue[activeNotificationIndex];
    if (!currentNotification?.id || viewedPopupIdsRef.current.has(currentNotification.id)) {
      return;
    }

    viewedPopupIdsRef.current.add(currentNotification.id);
    void markNotificationAsSeen(currentNotification.id);
  }, [activeNotificationIndex, markNotificationAsSeen, openPopup, popupQueue]);

  useEffect(() => {
    const latest = summary.latest_unread;
    if (!latest || !isTipNotification(latest)) {
      return undefined;
    }
    if (shownToastIdsRef.current.has(latest.id)) {
      return undefined;
    }

    let isHandled = false;

    const showToastOnFirstInteraction = () => {
      if (isHandled || shownToastIdsRef.current.has(latest.id)) {
        return;
      }

      isHandled = true;
      markToastAsShown(latest.id);
      if (navigator.vibrate) {
        navigator.vibrate(25);
      }

      toast(latest.title, {
        description: latest.message,
        action: getNotificationActionPath(latest)
          ? {
              label: 'Open',
              onClick: async () => {
                await markAsRead(latest.id);
                navigate(getNotificationActionPath(latest));
              },
            }
          : undefined,
      });
    };

    const handleFirstInteraction = () => {
      showToastOnFirstInteraction();
    };

    document.addEventListener('pointerdown', handleFirstInteraction, true);
    document.addEventListener('touchstart', handleFirstInteraction, true);
    document.addEventListener('keydown', handleFirstInteraction, true);

    return () => {
      document.removeEventListener('pointerdown', handleFirstInteraction, true);
      document.removeEventListener('touchstart', handleFirstInteraction, true);
      document.removeEventListener('keydown', handleFirstInteraction, true);
    };
  }, [markAsRead, markToastAsShown, navigate, summary.latest_unread]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        aria-label="Notifications"
        data-testid={buttonTestId}
        onClick={handleBellClick}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
      >
        <BellIcon
          hasNotification={summary.unread_count > 0}
          animateDot={summary.unread_count > 0}
          isMuted={summary.push_buckets?.operational_alerts_push === false || summary.push_buckets?.vibes_reminders_push === false}
        />
      </button>

      {openPopup && activeNotification && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            width: 332,
            maxWidth: 'calc(100vw - 24px)',
            zIndex: 40,
            marginTop: 10,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -10,
              right: 24,
              width: 24,
              height: 24,
              background: 'var(--surface)',
              borderTop: '1px solid var(--card-border)',
              borderLeft: '1px solid var(--card-border)',
              transform: 'rotate(45deg)',
              boxShadow: 'var(--premium-shadow-nested)',
            }}
          />
          <div
            data-testid={popupTestId}
            onClick={popupActionPath ? handlePopupActivate : undefined}
            onKeyDown={popupActionPath ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handlePopupActivate();
              }
            } : undefined}
            role={popupActionPath ? 'button' : undefined}
            tabIndex={popupActionPath ? 0 : undefined}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 28,
              boxShadow: 'var(--premium-shadow-main)',
              padding: '18px 18px 18px',
              position: 'relative',
              cursor: popupActionPath ? 'pointer' : 'default',
              transition: 'transform 180ms ease, box-shadow 180ms ease',
              overflow: 'hidden',
            }}
          >
            <div
              ref={sliderRef}
              data-testid={`${popupTestId}-slider`}
              onScroll={(event) => {
                const width = event.currentTarget.clientWidth || 1;
                const nextIndex = Math.round(event.currentTarget.scrollLeft / width);
                setActiveNotificationIndex(Math.max(0, Math.min(nextIndex, popupNotifications.length - 1)));
              }}
              style={{ display: 'flex', alignItems: 'stretch', gap: 0, height: sliderHeight === 'auto' ? 'auto' : sliderHeight, overflowX: popupNotifications.length > 1 ? 'auto' : 'hidden', overflowY: 'hidden', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none', transition: 'height 240ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            >
              {popupNotifications.map((notification) => {
                const listingMeta = buildListingMeta(notification);
                const notificationImage = notification?.metadata?.listing_image || '/allure-logo-mono.png';
                const dateRange = formatDateRange(notification);
                const isDetected = ['detected', 'confirmed_by_host'].includes(notification?.metadata?.status);
                const notificationTitle = isDetected ? 'Reservation detected' : notification.title;
                return (
                  <div key={notification.id} data-testid={`${popupTestId}-slide-${notification.id}`} style={{ minWidth: '100%', scrollSnapAlign: 'center' }}>
                    <div ref={(el) => { if (el) { contentRefs.current[notification.id] = el; } }}>
                    <p data-testid={`${popupTestId}-title-${notification.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: isDetected ? 21 : 18, fontWeight: 700, color: isDetected ? 'var(--status-success-text)' : 'var(--text)', lineHeight: 1.15, textAlign: 'center', margin: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      {notificationTitle}
                    </p>
                    {isReservationNotification(notification) ? (
                      <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <div data-testid={`${popupTestId}-image-shell-${notification.id}`} style={{ width: 62, height: 62, borderRadius: 18, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}>
                            <img src={notificationImage} alt={listingMeta.displayName} data-testid={`${popupTestId}-image-${notification.id}`} onLoad={measureActiveSlide} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          <div style={{ minWidth: 0, display: 'grid', gap: 4, flex: 1 }}>
                            <p data-testid={`${popupTestId}-listing-name-${notification.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {listingMeta.displayName}
                            </p>
                            <p data-testid={`${popupTestId}-listing-location-${notification.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', margin: 0, lineHeight: 1.1 }}>
                              {listingMeta.displayAddress || 'Location unavailable'}
                            </p>
                          </div>
                        </div>
                        <p data-testid={`${popupTestId}-dates-${notification.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text)', margin: 0, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {dateRange}
                        </p>
                      </div>
                    ) : (
                      <p data-testid={`${popupTestId}-message-${notification.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.28, textAlign: 'center', margin: '12px 0 0', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {notification.message}
                      </p>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
            {popupNotifications.length > 1 ? (
              <div data-testid={`${popupTestId}-dots`} style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14 }}>
                {popupNotifications.map((notification, index) => (
                  <motion.button
                    key={notification.id}
                    type="button"
                    data-testid={`${popupTestId}-dot-${index}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      const slider = sliderRef.current;
                      if (!slider) return;
                      slider.scrollTo({ left: slider.clientWidth * index, behavior: 'smooth' });
                      setActiveNotificationIndex(index);
                    }}
                    animate={showSwipeHint
                      ? { scale: [1, 1.7, 1], backgroundColor: ['var(--dot-inactive)', 'var(--accent-purple-strong)', 'var(--dot-inactive)'] }
                      : { scale: 1, backgroundColor: index === activeNotificationIndex ? 'var(--dot-active)' : 'var(--dot-inactive)' }}
                    transition={showSwipeHint
                      ? { duration: 0.6, delay: 0.14 * index, repeat: 1, repeatDelay: 0.55, ease: 'easeInOut' }
                      : { duration: 0.2 }}
                    style={{ width: 7, height: 7, borderRadius: '50%', border: 'none', padding: 0, background: index === activeNotificationIndex ? 'var(--dot-active)' : 'var(--dot-inactive)', cursor: 'pointer' }}
                  />
                ))}
              </div>
            ) : null}
            {popupActionPath ? <p data-testid={`${popupTestId}-hint`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-2)', textAlign: 'center', margin: '14px 0 0' }}>Tap to open</p> : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBellButton;